/**
 * EngagementPanel.tsx — the deal drawer's Engagement view (2026-09-23): the
 * re-engagement email sequence's state, the merged sent-items timeline, the
 * two ADMIN lanes that reach a patient — "Send content…" (a real email, behind
 * a confirm) and per-row "Copy for WhatsApp" (a clipboard copy, behind
 * nothing, because a copy is not a send) — and the ADMIN kill switch, "Revoke
 * unlock links" (every live link on the deal stops working; the next send
 * mints fresh — for when a link went to the wrong chat).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: engagement tab.
 * CTO-approved design boards, 2026-09-23 (Drawer / SendContent / SendRefused /
 * WhatsAppCopy). Mounts beside JourneyPanel behind the Overview tab's nested
 * Journey / Engagement tabs.
 *
 * ── ⚠️ THE FLOOR RULE: A COPY IS NOT A SEND ─────────────────────────────────
 * Copying a row copies `${message}\n${url}` FIRST, then fires the quiet
 * `link-copied` mark (fire-and-forget, void-swallowed — the CopyLinkButton
 * discipline: the person who copied must never see a telemetry error, and a
 * refused clipboard audits NOTHING), and then IMMEDIATELY opens the log
 * prompt. "Not sent yet" closes it and records nothing further; only "Log as
 * sent on WhatsApp" puts a touch on the timeline. The copy mark is a FLOOR,
 * never a count.
 *
 * ── ⚠️ THE REFUSAL STAYS IN THE DIALOG ──────────────────────────────────────
 * A send 409 (consent absent/withdrawn, content not published, no address) is
 * the SERVER'S sentence, shown VERBATIM inside the still-open dialog with the
 * send control dead until the choice changes — the close-as-lost lesson: a
 * dialog that closes on refusal reports the failure behind its own scrim,
 * outside its aria-modal subtree, and reads as a silent no-op.
 *
 * ── ROLES (R41 — the server is the control) ─────────────────────────────────
 * CLINICIAN + ADMIN both READ this panel; every control that reaches the
 * patient (send, copy, log) is ADMIN-only affordance over an ADMIN-only route.
 * Nothing money-shaped exists on this surface for either role.
 */
import { useCallback, useEffect, useId, useState, type CSSProperties } from 'react';
import { ApiError } from '../api/client';
import {
  getDealEngagement,
  getEngagementContent,
  getEngagementWhatsAppMessage,
  logEngagementWhatsApp,
  recordEngagementLinkCopied,
  revokeEngagementLinks,
  sendEngagementContent,
} from '../api/engagement';
import type {
  DealCard,
  DealEngagement,
  EngagementContentItem,
  EngagementWhatsAppMessage,
  UserRole,
} from '../api/types';
import { formatUtcDate, formatUtcDateTime } from '../lib/format';
import { copyToClipboard } from '../staff/clipboard';
import { SectionTitleInline } from '../staff/ui';
import { ConfirmDialog, DialogShell } from '../ui/ConfirmDialog';
import { Alert, Badge, Button, ErrorState, Field, Help, Select, Spinner, TextArea, type AlertTone, type BadgeTone } from '../ui/kit';
import {
  SEQUENCE_STATUS_LABEL,
  STEP_STATUS_LABEL,
  engagementTimeline,
  nextDueStep,
  rowCarriesCopy,
  rowContentId,
  rowLabel,
  stepDayLabel,
  stepProgressLine,
  stopReasonLabel,
  type EngagementTimelineRow,
} from './engagementModel';
import { ACTOR_ROLE_LABEL } from './stageClarity';
import { dealName } from './PipelineBoard';

/** The server's zod cap on the WhatsApp-log note — the field stops at it. */
const WHATSAPP_NOTE_MAX = 500;

/**
 * Sequence-status chip tones. `stopped` is PENDING amber (attention, not
 * alarm — a stop is routine bookkeeping, and the danger red would make every
 * consent withdrawal shout); `exhausted` is muted: the sequence simply ran
 * out. The words carry the meaning either way — never colour alone.
 */
const SEQUENCE_STATUS_TONE: Record<string, BadgeTone> = {
  active: 'positive',
  stopped: 'pending',
  completed: 'neutral',
  exhausted: 'muted',
};

type Notice = { tone: AlertTone; text: string };

/** What the open log prompt is about — set the moment a copy lands. */
interface LogPromptState {
  label: string;
  contentId: string | null;
  payload: EngagementWhatsAppMessage;
}

/* ── THE TIMELINE ROW CHIP ──────────────────────────────────────────────────
 * AUTO · EMAIL for an automated step, EMAIL for a staff content send,
 * WHATSAPP for a logged WhatsApp touch — provenance at a glance, with the
 * full words in the text so the chip is never the only signal. */
function rowChipText(row: EngagementTimelineRow): string {
  if (row.kind === 'step') return 'Auto · email';
  return row.touch.kind === 'whatsapp_logged' ? 'WhatsApp' : 'Email';
}

const rowChipStyle: CSSProperties = {
  background: 'var(--gps-surface-2)',
  color: 'var(--gps-muted)',
  border: '1px solid var(--gps-border)',
  borderRadius: 'var(--gps-r-pill)',
  fontSize: '10.5px',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  padding: '2px 8px',
  flex: 'none',
  whiteSpace: 'nowrap',
};

/** The right-hand status word a row ends with. */
function rowStatusWord(row: EngagementTimelineRow): string {
  if (row.kind === 'step') return STEP_STATUS_LABEL[row.step.status] ?? row.step.status;
  return row.touch.kind === 'whatsapp_logged' ? 'Logged' : 'Sent';
}

/* ── THE PANEL ──────────────────────────────────────────────────────────── */

export function EngagementPanel({ deal, role }: { deal: DealCard; role: UserRole }) {
  const [data, setData] = useState<DealEngagement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [notice, setNotice] = useState<Notice | null>(null);
  /** The row whose copy payload is being built — that one button goes quiet. */
  const [copyBusy, setCopyBusy] = useState<string | null>(null);
  const [logPrompt, setLogPrompt] = useState<LogPromptState | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  /** Names the "switched off" line the disabled Send button points at. */
  const sendOffId = useId();

  const dealId = deal.dealId;

  /**
   * The one read. `refresh` keeps the current timeline on screen while the
   * re-read runs (the outcome-outlives-the-refresh rule: a blanking re-read
   * after a log would unmount the list the new touch is about to appear in).
   */
  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial') => {
      if (mode === 'initial') setStatus('loading');
      try {
        setData(await getDealEngagement(dealId));
        setStatus('ready');
      } catch {
        // A failed REFRESH keeps the data already on screen — stale beats
        // blank, and the next action re-reads anyway.
        setStatus((prev) => (mode === 'refresh' && prev === 'ready' ? 'ready' : 'error'));
      }
    },
    [dealId],
  );
  useEffect(() => {
    void load('initial');
  }, [load]);

  /**
   * COPY FOR WHATSAPP — the pinned order, each step gated on the one before:
   *  1. POST whatsapp-message → the payload (a failure here is a plain error);
   *  2. copy `${message}\n${url}` — COPY-FIRST: nothing is audited and no
   *     prompt opens until the value actually reached the clipboard, because
   *     auditing a copy that never happened puts a contact mark on a record
   *     nobody made;
   *  3. fire-and-forget link-copied (void-swallow);
   *  4. IMMEDIATELY open the log prompt.
   */
  async function copyForWhatsApp(row: EngagementTimelineRow) {
    if (copyBusy) return;
    setCopyBusy(row.id);
    setNotice(null);
    try {
      const contentId = rowContentId(row);
      const payload = await getEngagementWhatsAppMessage(dealId, contentId ?? undefined);
      const ok = await copyToClipboard(`${payload.message}\n${payload.url}`);
      if (!ok) {
        setNotice({ tone: 'danger', text: 'The browser refused the clipboard — nothing was copied and nothing was recorded.' });
        return;
      }
      recordEngagementLinkCopied(dealId);
      setLogPrompt({ label: rowLabel(row), contentId, payload });
    } catch (err) {
      // ⚠️ THE SERVER'S REFUSAL IS SHOWN VERBATIM (the call rides parseError).
      // A test-marked card's 409 and a closed card's nothing-to-unlock 409 are
      // complete instructions that no retry can fix — the old catch discarded
      // `err` and told the admin to 'Try again' forever. The house sentence is
      // only for a failure the server did not word (network, 500).
      setNotice({
        tone: 'danger',
        text:
          err instanceof ApiError && err.detail
            ? err.detail
            : 'We could not build the WhatsApp message — nothing was copied. Try again.',
      });
    } finally {
      setCopyBusy(null);
    }
  }

  if (status === 'loading') {
    return (
      <div style={{ marginTop: 'var(--gps-s-4)' }}>
        <Spinner label="Loading engagement…" />
      </div>
    );
  }
  if (status === 'error' || !data) {
    return (
      <div style={{ marginTop: 'var(--gps-s-4)' }}>
        <ErrorState message="We could not load this deal's engagement." onRetry={() => void load('initial')} />
      </div>
    );
  }

  const enrolment = data.enrolment;
  const rows = engagementTimeline(data);
  const isAdmin = role === 'ADMIN';
  const next = enrolment ? nextDueStep(enrolment) : null;

  return (
    <section
      aria-labelledby="deal-engagement-title"
      style={{ marginTop: 'var(--gps-s-4)', paddingTop: 'var(--gps-s-3)', borderTop: '1px solid var(--gps-border)' }}
    >
      <div style={{ marginBottom: 'var(--gps-s-3)' }}>
        <SectionTitleInline>
          <span id="deal-engagement-title">Engagement</span>
        </SectionTitleInline>
      </div>

      {/* ── THE ENROLMENT CARD ─────────────────────────────────────────────
          The sequence's own state: status chip (+ the stop reason in words),
          "Step N of M" off the server's stepsTotal, and the next due step.
          When there is NO enrolment the card says so honestly — and when the
          reason is visible from here (no email consent) it names it, because
          an empty card with no why reads as a rendering fault. */}
      <div
        style={{
          border: '1px solid var(--gps-border)',
          borderRadius: 'var(--gps-r-md)',
          background: 'var(--gps-surface-2)',
          padding: '12px 14px',
          display: 'grid',
          gap: '6px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-2)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--gps-muted)' }}>
            Email sequence
          </span>
          {enrolment ? (
            <>
              <Badge tone={SEQUENCE_STATUS_TONE[enrolment.status] ?? 'neutral'}>
                {SEQUENCE_STATUS_LABEL[enrolment.status] ?? enrolment.status}
              </Badge>
              {/* The stop reason, in words beside the chip — "Stopped" alone
                  answers nothing an admin can act on. */}
              {enrolment.status === 'stopped' && stopReasonLabel(enrolment.stopReason) ? (
                <span style={{ fontSize: '12.5px', color: 'var(--gps-muted)' }}>{stopReasonLabel(enrolment.stopReason)}</span>
              ) : null}
            </>
          ) : null}
        </div>
        {enrolment ? (
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gps-text)' }}>
            {stepProgressLine(enrolment)}
            {next ? (
              <span style={{ fontWeight: 400, color: 'var(--gps-muted)' }}>
                {` · next: ${stepDayLabel(next)}, due ${formatUtcDate(next.dueAt)}`}
              </span>
            ) : null}
          </div>
        ) : (
          <div style={{ fontSize: '13.5px', color: 'var(--gps-text)' }}>
            No email sequence for this card.
            {data.consent.email === false ? (
              <span style={{ color: 'var(--gps-muted)' }}>
                {' '}
                The patient has no marketing email consent on record, so the sequence cannot email them.
              </span>
            ) : null}
          </div>
        )}
      </div>

      {/* ── THE ADMIN ACTION ROW ─────────────────────────────────────────── */}
      {isAdmin ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-3)', flexWrap: 'wrap', marginTop: 'var(--gps-s-3)' }}>
          {/* `width: 'auto'` — buttonStyle() is width:100%, and a full-width
              child in a wrapping flex row takes the line to itself. */}
          {/* THE GUIDE-EMAIL SWITCH (clinic ENGAGEMENT_CONTENT_SEND_ENABLED,
              default OFF, 2026-09-25). Off, the button stays visible but
              disabled and names why, rather than opening a dialog whose only
              possible answer is the server's 409. `=== true` so a read that
              predates the field (an older clinic) reads as off. The server
              refuses the send regardless (R41); this is the affordance only. */}
          <Button
            type="button"
            variant="primary"
            onClick={() => setSendOpen(true)}
            disabled={data.contentEmailEnabled !== true}
            aria-describedby={data.contentEmailEnabled === true ? undefined : sendOffId}
            style={{ width: 'auto' }}
          >
            Send content…
          </Button>
          {data.contentEmailEnabled === true ? null : (
            <Help style={{ margin: 0 }}>
              <span id={sendOffId}>
                Emailing guides is switched off until the marketing-email checks are complete, so nothing can be sent
                from here yet.
              </span>
            </Help>
          )}
          <Help style={{ margin: 0 }}>
            Every sent item below carries its own Copy for WhatsApp — copying prompts you to log the send.
          </Help>
          {/* THE WRONG-CHAT KILL SWITCH — small, destructive-inked, pushed to
              the row's far end: a recovery control, never a workflow one. The
              danger colour is the consequence (every live link stops working),
              and the confirm carries the full copy before anything happens. */}
          <Button
            type="button"
            variant="ghost"
            onClick={() => setRevokeOpen(true)}
            style={{
              width: 'auto',
              minHeight: '36px',
              padding: '5px 10px',
              fontSize: '12.5px',
              color: 'var(--gps-danger-text)',
              marginLeft: 'auto',
              flex: 'none',
            }}
          >
            Revoke unlock links
          </Button>
        </div>
      ) : null}

      {notice ? (
        // A failure alerts (the reader may have looked away); the quiet
        // successes announce politely.
        <Alert tone={notice.tone} role={notice.tone === 'danger' ? 'alert' : 'status'} style={{ marginTop: 'var(--gps-s-3)' }}>
          {notice.text}
        </Alert>
      ) : null}

      {/* ── THE TIMELINE — steps and touches merged, newest first ────────── */}
      <h3
        style={{
          margin: 'var(--gps-s-4) 0 var(--gps-s-2)',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: 'var(--gps-muted)',
        }}
      >
        Engagement timeline
      </h3>
      {rows.length === 0 ? (
        <Help style={{ margin: 0 }}>Nothing has been sent to this patient yet.</Help>
      ) : (
        <ol aria-label="Engagement timeline" style={{ listStyle: 'none', margin: 0, padding: 0, borderTop: '1px solid var(--gps-border)' }}>
          {rows.map((row) => (
            <li
              key={row.id}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 'var(--gps-s-3)',
                padding: '10px 2px',
                borderBottom: '1px solid var(--gps-border)',
                fontSize: '13.5px',
              }}
            >
              <span style={{ color: 'var(--gps-muted)', fontSize: '12.5px', flex: 'none', width: '118px' }}>
                {formatUtcDateTime(row.at)}
              </span>
              <span style={rowChipStyle}>{rowChipText(row)}</span>
              <span style={{ minWidth: 0, fontWeight: row.kind === 'step' ? 600 : 500 }}>
                {rowLabel(row)}
                {/* WHO made a manual touch — the ROLE, never a raw UUID: the
                    wire carries actorUserId + actorRole, and a UUID in a "who
                    did this" slot reads to staff as a system action. */}
                {row.kind === 'touch' && ACTOR_ROLE_LABEL[row.touch.actorRole] ? (
                  <span style={{ color: 'var(--gps-muted)', fontWeight: 400 }}>{` · logged by ${ACTOR_ROLE_LABEL[row.touch.actorRole]}`}</span>
                ) : null}
                {row.kind === 'touch' && row.touch.note ? (
                  // The note is free text (R19: deal surface only) — a plain
                  // text node, never markup.
                  <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--gps-muted)', fontWeight: 400 }}>{row.touch.note}</span>
                ) : null}
              </span>
              <span style={{ marginLeft: 'auto', flex: 'none', color: 'var(--gps-muted)', fontSize: '12.5px', fontWeight: 600 }}>
                {rowStatusWord(row)}
              </span>
              {/* EVERY SENT ITEM carries its own copy — ADMIN affordance over
                  an ADMIN-only route. The accessible name carries the row so
                  a list of identical buttons is still tellable apart. */}
              {isAdmin && rowCarriesCopy(row) ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => void copyForWhatsApp(row)}
                  disabled={copyBusy !== null}
                  aria-label={`Copy for WhatsApp — ${rowLabel(row)}`}
                  style={{ width: 'auto', minHeight: '36px', padding: '5px 10px', fontSize: '12.5px', flex: 'none' }}
                >
                  {copyBusy === row.id ? 'Copying…' : 'Copy for WhatsApp'}
                </Button>
              ) : null}
            </li>
          ))}
        </ol>
      )}

      {logPrompt ? (
        <WhatsAppLogDialog
          dealId={dealId}
          prompt={logPrompt}
          onClose={() => setLogPrompt(null)}
          onLogged={() => {
            setLogPrompt(null);
            setNotice({ tone: 'success', text: 'Logged — the WhatsApp send is on the timeline.' });
            void load('refresh');
          }}
        />
      ) : null}

      {sendOpen ? (
        <SendContentDialog
          deal={deal}
          consentEmail={data.consent.email}
          onCancel={() => setSendOpen(false)}
          onSent={() => {
            setSendOpen(false);
            setNotice({ tone: 'success', text: 'Content sent — the patient has been emailed their unlock link.' });
            void load('refresh');
          }}
        />
      ) : null}

      {revokeOpen ? (
        <RevokeLinksDialog
          dealId={dealId}
          onCancel={() => setRevokeOpen(false)}
          onDone={(revoked) => {
            setRevokeOpen(false);
            // Zero is the idempotent steady state, not an empty result — it
            // gets the success tone and words saying nothing was outstanding
            // (the RecallUnvouchedAction rule), never an error an admin would
            // press again and then escalate.
            setNotice({
              tone: 'success',
              text:
                revoked === 0
                  ? 'No live links to revoke — nothing was changed.'
                  : `${revoked} ${revoked === 1 ? 'link' : 'links'} revoked — every unlock link for this card has stopped working. The next send makes a fresh one.`,
            });
            void load('refresh');
          }}
        />
      ) : null}
    </section>
  );
}

/* ── THE REVOKE-LINKS CONFIRM — the wrong-chat kill switch ──────────────────
 *
 * ADMIN-only (the panel gates the opener exactly as it gates Send content;
 * the route is `@Roles('ADMIN')` — R41). tone="danger" because revoking is
 * the consequence rule's case: every link sent for this card stops working
 * the moment it lands. The copy is benign-but-clear — it names what stops
 * (every sent link), what recovers it (the next send mints fresh) and what
 * it is for (a link that went to the wrong chat). A failure stays in the
 * OPEN dialog with the server's sentence verbatim (`ApiError.detail` — e.g.
 * 'Unknown deal'), the estate's refusal grammar.
 */
function RevokeLinksDialog({
  dealId,
  onCancel,
  onDone,
}: {
  dealId: string;
  onCancel: () => void;
  onDone: (revoked: number) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revoke() {
    // Both ends guard the double submit — `busy` on the dialog AND here.
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await revokeEngagementLinks(dealId);
      onDone(result.revoked);
    } catch (err) {
      setError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — we could not revoke the links. They are unchanged; try again.',
      );
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog
      open
      tone="danger"
      title="Revoke unlock links?"
      confirmLabel={busy ? 'Revoking…' : 'Revoke unlock links'}
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => void revoke()}
      body={
        <>
          <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13.5px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
            Every unlock link sent for this card stops working straight away — use this when a link went to the wrong
            chat. The next send makes a fresh link, and nothing else on the record changes.
          </p>
          {error ? (
            <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-2)' }}>
              {error}
            </Alert>
          ) : null}
        </>
      }
    />
  );
}

/* ── THE POST-COPY LOG PROMPT (WhatsAppCopy board) ──────────────────────────
 *
 * Opens the moment anything is copied. "Not sent yet" closes it and does
 * NOTHING ELSE — the quiet copy mark already went out behind the copy, and it
 * is a floor, never a count. Only "Log as sent on WhatsApp" posts the touch.
 * A failed log stays OPEN with the failure inside the panel (the message is
 * still on the clipboard, and closing would discard the note just typed).
 */
function WhatsAppLogDialog({
  dealId,
  prompt,
  onClose,
  onLogged,
}: {
  dealId: string;
  prompt: LogPromptState;
  onClose: () => void;
  onLogged: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noteId = useId();
  const errorId = useId();

  async function log() {
    // Both ends guard the double submit — `busy` on the buttons AND here.
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const trimmed = note.trim();
      await logEngagementWhatsApp(dealId, {
        ...(prompt.contentId ? { contentId: prompt.contentId } : {}),
        ...(trimmed ? { note: trimmed } : {}),
      });
      onLogged();
    } catch (err) {
      setError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — we could not log this. The message is still on your clipboard; try again.',
      );
      setBusy(false);
    }
  }

  return (
    <DialogShell title="Sending this on WhatsApp?" onCancel={busy ? () => {} : onClose}>
      {/* The copy confirmation, restated where the reader now is. */}
      <p role="status" style={{ margin: '0 0 var(--gps-s-2)', fontSize: '13px', color: 'var(--gps-green-text)', fontWeight: 600 }}>
        {`Copied — message and link for “${prompt.label}”`}
      </p>
      <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13.5px', color: 'var(--gps-text)', lineHeight: 1.5 }}>
        Paste it into the WhatsApp thread, then log it here so the engagement timeline shows the touch.
      </p>
      {/* The exact payload that is on the clipboard — what "it" means. */}
      <div
        style={{
          background: 'var(--gps-surface-2)',
          borderRadius: 'var(--gps-r-md)',
          padding: '10px 12px',
          fontSize: '13px',
          lineHeight: 1.5,
          marginBottom: 'var(--gps-s-3)',
        }}
      >
        {prompt.payload.message}
        <span style={{ display: 'block', color: 'var(--gps-link)', overflowWrap: 'anywhere' }}>{prompt.payload.url}</span>
      </div>
      <Field label="Anything to add? (optional)" htmlFor={noteId} hint={`Up to ${WHATSAPP_NOTE_MAX} characters.`}>
        <TextArea id={noteId} value={note} maxLength={WHATSAPP_NOTE_MAX} onChange={(e) => setNote(e.target.value)} rows={2} />
      </Field>
      {error ? (
        <Alert tone="danger" role="alert" id={errorId} style={{ marginBottom: 'var(--gps-s-2)' }}>
          {error}
        </Alert>
      ) : null}
      <Help style={{ margin: '0 0 var(--gps-s-2)' }}>
        Only &ldquo;Log as sent&rdquo; puts a WhatsApp touch on the timeline &mdash; a copy is not a send.
      </Help>
      <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginTop: 'var(--gps-s-2)' }}>
        <Button
          type="button"
          variant="primary"
          onClick={() => void log()}
          disabled={busy}
          aria-describedby={error ? errorId : undefined}
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}
        >
          {busy ? 'Logging…' : 'Log as sent on WhatsApp'}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}>
          Not sent yet
        </Button>
      </div>
    </DialogShell>
  );
}

/**
 * The one refusal the server itself marks RETRYABLE: its transient send
 * failure ends with the literal instruction 'Try again.' ("…nothing was sent.
 * Try again."). Every other 409 on this route — consent, unpublished, test
 * record, nothing-to-unlock — is a standing fact no repeat press can change,
 * so those stay terminal until the operator's CHOICE changes.
 */
function isRetryableRefusal(sentence: string): boolean {
  return sentence.trimEnd().endsWith('Try again.');
}

/* ── THE SEND-CONTENT DIALOG (SendContent / SendRefused boards) ─────────────
 *
 * The estate's confirm grammar for anything that CONTACTS a patient: name the
 * recipient (the title), name what lands (the unlock-link email), name what
 * does not change (no PDF attached, nothing else on the record). Built on
 * ConfirmDialog so it inherits the busy lock and the escape/cancel behaviour;
 * `confirmDisabled` carries the extra gates (no piece picked, refusal
 * standing).
 */
function SendContentDialog({
  deal,
  consentEmail,
  onCancel,
  onSent,
}: {
  deal: DealCard;
  consentEmail: boolean;
  onCancel: () => void;
  onSent: () => void;
}) {
  const [items, setItems] = useState<EngagementContentItem[] | null>(null);
  const [listStatus, setListStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  /** The server's 409 sentence — VERBATIM, in the dialog, with send dead. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pickId = useId();
  const refusalId = useId();

  useEffect(() => {
    let live = true;
    getEngagementContent()
      .then((list) => {
        if (!live) return;
        setItems(list.items);
        setListStatus('ready');
      })
      .catch(() => {
        if (live) setListStatus('error');
      });
    return () => {
      live = false;
    };
  }, []);

  // PUBLISHED ONLY in the picker: draft is not ready and retired is not
  // sendable (the server 409s both; offering them invites the refusal).
  const published = (items ?? []).filter((i) => i.status === 'published');

  async function send() {
    if (busy || !selected) return;
    setBusy(true);
    setRefusal(null);
    setError(null);
    try {
      await sendEngagementContent(deal.dealId, selected);
      onSent();
    } catch (err) {
      // ⚠️ THE 409 IS THE SERVER'S OWN SENTENCE AND THE DIALOG STAYS OPEN —
      // consent withdrawn, content not published, no address are each a
      // complete instruction, and closing would read as a silent no-op.
      if (err instanceof ApiError && err.status === 409 && err.detail) {
        setRefusal(err.detail);
      } else {
        setError('Sorry — we could not send this. Nothing was sent; try again.');
      }
      setBusy(false);
    }
  }

  // ⚠️ A TERMINAL refusal deadens Send until the choice changes; a refusal
  // that ITSELF says 'Try again.' must NOT — the button it names is the only
  // retry control in the dialog, and disabling it until the picker moved left
  // the operator re-picking the same piece to obey the server's own sentence.
  // `send()` clears the standing refusal on press, so the retry is honest.
  const refusalIsTerminal = refusal !== null && !isRetryableRefusal(refusal);

  return (
    <ConfirmDialog
      open
      title={`Send content to ${dealName(deal)}`}
      confirmLabel={busy ? 'Sending…' : 'Send email'}
      busy={busy}
      // No piece picked, or a TERMINAL refusal standing: the send control is
      // dead until the CHOICE changes (picking again clears the refusal
      // below). The retryable refusal keeps Send alive — see the branch above.
      confirmDisabled={selected === '' || refusalIsTerminal || listStatus !== 'ready'}
      confirmDescribedBy={refusal ? refusalId : undefined}
      onCancel={onCancel}
      onConfirm={() => void send()}
      body={
        <>
          {listStatus === 'loading' ? <Spinner label="Loading the content library…" /> : null}
          {listStatus === 'error' ? (
            <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
              We could not load the content library — close this and try again.
            </Alert>
          ) : null}
          {listStatus === 'ready' ? (
            <Field
              label="What are we sending?"
              htmlFor={pickId}
              hint="Only published pieces appear here. The PDF is never attached — the email carries the patient's unlock link, and the download opens once they're set up."
            >
              <Select
                id={pickId}
                value={selected}
                onChange={(e) => {
                  setSelected(e.target.value);
                  // A new choice retires the old refusal — it was about the
                  // previous attempt, and the send control comes back with it.
                  setRefusal(null);
                }}
              >
                <option value="">Choose a piece…</option>
                {published.map((item) => (
                  <option key={item.id} value={item.id}>
                    {`${item.title} (PDF)`}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {listStatus === 'ready' && published.length === 0 ? (
            <Help style={{ marginTop: 0 }}>Nothing is published yet, so there is nothing to send.</Help>
          ) : null}
          {/* The consent line states only what this read holds. TRUE is a
              fact worth showing before a send; FALSE is stated quietly and
              gates nothing — the server re-checks and its refusal carries the
              authoritative words (R41). */}
          {consentEmail ? (
            <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13px', color: 'var(--gps-green-text)' }}>
              Marketing email consent is on record for this patient.
            </p>
          ) : (
            <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13px', color: 'var(--gps-muted)' }}>
              No marketing email consent is on record for this patient.
            </p>
          )}
          {refusal ? (
            <Alert tone="danger" role="alert" id={refusalId} style={{ marginBottom: 'var(--gps-s-3)' }}>
              {refusal}
            </Alert>
          ) : null}
          {error ? (
            <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
              {error}
            </Alert>
          ) : null}
        </>
      }
    />
  );
}
